/**
 * Pure logic of the perimeter selector (HOMCO-1249). The tree is built frontend
 * side from GET /terrains: root « Tous les pays » → one node per distinct `pays`
 * → the terrains of that pays (sorted by libellé). Selection is kept NORMALIZED
 * (no node covered by another), and serialized to the backend's
 * `PerimeterNode[]` shape. No React here — see the Drawer/Chips/Summary.
 */

export type PerimeterType = 'all' | 'pays' | 'terrain';

/** One node of a user's perimeter, as the backend stores it (`code` null ⇔ `all`). */
export interface PerimeterNode {
  type: PerimeterType;
  code: string | null;
}

/** A terrain row of GET /terrains (only the fields the tree needs). */
export interface TerrainInput {
  codeterrain: string;
  /** Nullable in the DB; the tree falls back to `codeterrain` when empty. */
  libelle: string | null;
  pays: string;
}

export interface TreeNode {
  /** Unique in the tree: `all` | `pays:<pays>` | `terrain:<codeterrain>`. */
  id: string;
  type: PerimeterType;
  /** null for the root `all`, the pays value for a pays, the codeterrain for a terrain. */
  code: string | null;
  label: string;
  children: TreeNode[];
}

export interface PerimeterTree {
  root: TreeNode;
  byId: Record<string, TreeNode>;
  parentOf: Record<string, string | null>;
  /** `${type}:${normCode(code)}` → node id, for backend-matching resolution. */
  byKey: Record<string, string>;
}

const ROOT_ID = 'all';
const paysId = (pays: string) => `pays:${pays}`;
const terrainId = (codeterrain: string) => `terrain:${codeterrain}`;

const countryNames = new Intl.DisplayNames(['fr'], { type: 'region' });
function resolveCountryLabel(code: string): string {
  try {
    const name = countryNames.of(code);
    return name && name !== code ? `${code} – ${name}` : code;
  } catch {
    return code;
  }
}

// Match the backend `compareKey` (perimeter-node.vo.ts): codes compare
// case-insensitively with the trailing space trimmed, so a stored `{pays:'ml'}` or
// `'ML '` still resolves to the `ML` tree node instead of silently vanishing.
const normCode = (code: string) => code.trimEnd().toLowerCase();
const nodeKey = (type: PerimeterType, code: string) => `${type}:${normCode(code)}`;

export function buildTree(terrains: TerrainInput[]): PerimeterTree {
  // A terrain node built from a GET /terrains row — shared by the terrains nested under
  // a pays and the country-less / ambiguous terrains placed directly under the root.
  const terrainNode = (terrain: TerrainInput): TreeNode => ({
    id: terrainId(terrain.codeterrain),
    type: 'terrain' as const,
    code: terrain.codeterrain,
    label: terrain.libelle && terrain.libelle.trim()
      ? `${terrain.codeterrain} – ${terrain.libelle}`
      : terrain.codeterrain,
    children: [],
  });
  const byLabel = (a: TreeNode, b: TreeNode) => a.label.localeCompare(b.label);

  // Pass 1 — a `codeterrain` is only indexed, not unique: rows that disagree on the
  // country are ambiguous and, like the backend (TerrainsKyselyRepository.findPaysByCodes),
  // must never be nested under a pays (selecting a country must not cover them). Blank
  // countries never participate in this mapping.
  const paysOfTerrain = new Map<string, string>(); // normCode(codeterrain) → normCode(pays)
  const conflicting = new Set<string>(); // normCode(codeterrain)
  for (const terrain of terrains) {
    const paysKey = normCode(terrain.pays);
    if (paysKey === '') continue;
    const terrainKey = normCode(terrain.codeterrain);
    const known = paysOfTerrain.get(terrainKey);
    if (known === undefined) paysOfTerrain.set(terrainKey, paysKey);
    else if (known !== paysKey) conflicting.add(terrainKey);
  }

  // Pass 2 — group terrains under a representative pays, merging country codes that
  // differ only by case / trailing space (matching the backend `compareKey`), so a
  // stored `{pays:'ml'}` / `'ML '` can't split into two colliding branches. Country-less
  // (blank pays — rejected on save) and ambiguous terrains go straight under the root.
  // Terrains are deduplicated by code (per pays, and across the standalone bucket).
  const byPays = new Map<string, TerrainInput[]>(); // normCode(pays) → terrains
  const paysDisplay = new Map<string, string>(); // normCode(pays) → representative raw code
  const seenInPays = new Set<string>(); // `${paysKey}\u0000${terrainKey}`
  const standalone: TreeNode[] = [];
  const seenStandalone = new Set<string>(); // normCode(codeterrain)
  for (const terrain of terrains) {
    const paysKey = normCode(terrain.pays);
    const terrainKey = normCode(terrain.codeterrain);
    if (paysKey === '' || conflicting.has(terrainKey)) {
      if (!seenStandalone.has(terrainKey)) {
        seenStandalone.add(terrainKey);
        standalone.push(terrainNode(terrain));
      }
      continue;
    }
    if (!paysDisplay.has(paysKey)) paysDisplay.set(paysKey, terrain.pays);
    const dedupeKey = `${paysKey}\u0000${terrainKey}`;
    if (seenInPays.has(dedupeKey)) continue;
    seenInPays.add(dedupeKey);
    const bucket = byPays.get(paysKey) ?? [];
    bucket.push(terrain);
    byPays.set(paysKey, bucket);
  }

  const paysNodes: TreeNode[] = [...byPays.keys()]
    .map(paysKey => {
      const pays = paysDisplay.get(paysKey) as string;
      return {
        id: paysId(pays),
        type: 'pays' as const,
        code: pays,
        label: resolveCountryLabel(pays),
        children: (byPays.get(paysKey) ?? []).map(terrainNode).sort(byLabel),
      };
    })
    .sort(byLabel);

  const root: TreeNode = {
    id: ROOT_ID,
    type: 'all',
    code: null,
    // Fallback only — the rendering components localize the `all` node via
    // t('perimeter.all') (label, accessible name and search), so this is never shown.
    label: 'Tous les pays',
    children: [...paysNodes, ...standalone.sort(byLabel)],
  };

  const byId: Record<string, TreeNode> = {};
  const parentOf: Record<string, string | null> = {};
  const byKey: Record<string, string> = {};
  const walk = (node: TreeNode, parent: string | null) => {
    byId[node.id] = node;
    parentOf[node.id] = parent;
    if (node.code != null) byKey[nodeKey(node.type, node.code)] = node.id;
    node.children.forEach(child => walk(child, node.id));
  };
  walk(root, null);

  return { root, byId, parentOf, byKey };
}

/** Resolve a stored node to a tree id (backend-matching), or undefined if unknown. */
function resolveNodeId(node: PerimeterNode, tree: PerimeterTree): string | undefined {
  if (node.type === 'all') return ROOT_ID;
  if (!node.code) return undefined;
  return tree.byKey[nodeKey(node.type, node.code)];
}

/** Stored nodes that no longer match any tree node (deleted terrain, unknown code). */
export function unresolvedNodes(nodes: PerimeterNode[], tree: PerimeterTree): PerimeterNode[] {
  return nodes.filter(node => resolveNodeId(node, tree) === undefined);
}

/** Ids of every strict descendant of `id`. */
export function descendants(tree: PerimeterTree, id: string): string[] {
  const out: string[] = [];
  const node = tree.byId[id];
  if (!node) return out;
  const walk = (n: TreeNode) => {
    for (const child of n.children) {
      out.push(child.id);
      walk(child);
    }
  };
  walk(node);
  return out;
}

/** Ancestors of `id`, nearest first (excludes `id`). */
function ancestors(tree: PerimeterTree, id: string): string[] {
  const out: string[] = [];
  let parent = tree.parentOf[id] ?? null;
  while (parent) {
    out.push(parent);
    parent = tree.parentOf[parent] ?? null;
  }
  return out;
}

/** The selected (retained) + inherited nodes — everything the selection covers. */
export function coverage(selected: string[], tree: PerimeterTree): Set<string> {
  const covered = new Set<string>();
  for (const id of selected) {
    if (!tree.byId[id]) continue;
    covered.add(id);
    descendants(tree, id).forEach(d => covered.add(d));
  }
  return covered;
}

/** Number of terrain-type nodes covered by `selected` (pays and root excluded). */
export function terrainCoverageCount(selected: string[], tree: PerimeterTree): number {
  let count = 0;
  for (const id of coverage(selected, tree)) {
    if (tree.byId[id]?.type === 'terrain') count++;
  }
  return count;
}

/** Keep only retained nodes not covered by another retained node; dedupe; drop unknowns. */
export function normalize(selected: string[], tree: PerimeterTree): string[] {
  const unique = [...new Set(selected)].filter(id => tree.byId[id]);
  return unique.filter(
    id => !unique.some(other => other !== id && descendants(tree, other).includes(id)),
  );
}

export function toggleNode(selected: string[], tree: PerimeterTree, nodeId: string): string[] {
  if (!tree.byId[nodeId]) return selected;

  // 1. Retained → uncheck (descendants are not restored).
  if (selected.includes(nodeId)) {
    return selected.filter(id => id !== nodeId);
  }

  const covered = coverage(selected, tree);

  // 2. Not covered → check, absorbing any retained descendants.
  if (!covered.has(nodeId)) {
    return normalize([...selected, nodeId], tree);
  }

  // 3. Inherited → drop the covering ancestor A and retain the siblings along the
  //    path from A down to nodeId, excluding nodeId (and its own descendants).
  const nearestFirst = ancestors(tree, nodeId); // [parent, …, A, …, root]
  const ancestorA = nearestFirst.find(a => selected.includes(a));
  if (!ancestorA) return selected; // defensive: inherited implies a retained ancestor

  // Path A → … → parent(nodeId) → nodeId.
  const path = [...nearestFirst.slice(0, nearestFirst.indexOf(ancestorA) + 1).reverse(), nodeId];

  const siblings: string[] = [];
  for (let i = 0; i < path.length - 1; i++) {
    const parent = tree.byId[path[i]];
    const nextOnPath = path[i + 1];
    for (const child of parent.children) {
      if (child.id !== nextOnPath) siblings.push(child.id);
    }
  }

  return normalize([...selected.filter(id => id !== ancestorA), ...siblings], tree);
}

export function toPayload(selected: string[], tree: PerimeterTree): PerimeterNode[] {
  return normalize(selected, tree).map(id => {
    const node = tree.byId[id];
    return { type: node.type, code: node.code };
  });
}

export function fromPayload(nodes: PerimeterNode[], tree: PerimeterTree): string[] {
  const ids = nodes
    .map(node => resolveNodeId(node, tree))
    .filter((id): id is string => id !== undefined);
  return normalize(ids, tree);
}

/**
 * Merge a drawer selection (`applied`, only nodes the tree can resolve) back with the
 * stored nodes the current tree CANNOT resolve, so applying the drawer never silently
 * drops a stored node outside the visible terrain tree (e.g. a deleted terrain, or one
 * the caller can't see). An « all » selection supersedes everything, so it replaces the
 * list outright. Used by UserForm's perimeter drawer `onApply`.
 */
export function mergeAppliedNodes(
  applied: PerimeterNode[],
  current: PerimeterNode[],
  tree: PerimeterTree,
): PerimeterNode[] {
  if (applied.some(node => node.type === 'all')) return applied;
  return [...applied, ...unresolvedNodes(current, tree)];
}
